import type { CurrentUser, Order, Vendor } from '@arbuz/shared';
import { formatMoney, makeT } from '../i18n';
import { haptic } from '../tg';
import { ThemedIcon, type ThemedIconName } from './clientUi';
import { EmptyState } from './vendorUi';

interface Props {
  user: CurrentUser;
  activeOrders: Order[] | null;
  ordersError: string | null;
  vendor: Vendor | null;
  vendorError: string | null;
  onOpenOrder: (id: number) => void;
  onOrders: () => void;
  onProducts: () => void;
  onZones: () => void;
  onProfile: () => void;
  onRetryOrders: () => void;
  onRetryVendor: () => void;
}

function IconCircle({ icon }: { icon: ThemedIconName }) {
  return (
    <span className="courier-icon-circle" aria-hidden="true">
      <ThemedIcon name={icon} />
    </span>
  );
}

function QuickAction({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: ThemedIconName;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="card courier-quick-card"
      onClick={() => {
        haptic('light');
        onClick();
      }}
    >
      <IconCircle icon={icon} />
      <h3>{title}</h3>
      <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
        {subtitle}
      </p>
    </button>
  );
}

export function CourierHome({
  user,
  activeOrders,
  ordersError,
  vendor,
  vendorError,
  onOpenOrder,
  onOrders,
  onProducts,
  onZones,
  onProfile,
  onRetryOrders,
  onRetryVendor,
}: Props) {
  const t = makeT(user.locale);
  const next = activeOrders?.[0] ?? null;
  const readyDone = vendor
    ? [vendor.products_count > 0, vendor.zones_count > 0, vendor.open_days_count > 0].filter(Boolean).length
    : 0;

  return (
    <main className="page courier-page">
      <div className="courier-topline">
        <div>
          <p className="courier-eyebrow">{t('executorLabel')}</p>
          <h1 className="courier-title">{vendor?.display_name ?? user.first_name ?? t('executorLabel')}</h1>
        </div>
      </div>

      {ordersError ? (
        <EmptyState
          tone="error"
          title={t('ordersLoadError')}
          text={ordersError}
          actionLabel={t('retry')}
          onAction={onRetryOrders}
        />
      ) : activeOrders === null ? (
        <p className="muted">{t('loading')}</p>
      ) : next ? (
        <article className="card courier-hero-order">
          <div className="courier-between">
            <div>
              <p className="courier-order-number">
                {t('courierOrder')} #{next.public_number}
              </p>
              <h2 className="courier-order-title">
                {next.slot_date}, {next.slot_start}-{next.slot_end}
              </h2>
            </div>
            <span className="courier-pill">{t('nextOrder')}</span>
          </div>
          <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-sm)' }}>
            {next.address_text}
          </p>
          <div className="courier-between" style={{ marginTop: 'var(--s-3)', alignItems: 'center' }}>
            <div>
              <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
                {next.items.length} {t('pcs')}
              </p>
              <strong className="courier-price">
                {formatMoney(next.total, user.locale)} {t('sum')}
              </strong>
            </div>
            <button type="button" className="btn-primary" onClick={() => onOpenOrder(next.id)}>
              {t('openOrder')}
            </button>
          </div>
        </article>
      ) : (
        <EmptyState title={t('courierNoActive')} text={t('courierNoActiveHint')} />
      )}

      <div className="courier-metric-grid">
        <section className="card courier-metric">
          <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
            {t('activeOrders')}
          </p>
          <strong>{activeOrders?.length ?? 0}</strong>
        </section>
        <section className="card courier-metric">
          <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
            {t('vendorStatus')}
          </p>
          <strong style={{ color: vendor?.is_active ? 'var(--c-primary-text)' : 'var(--c-warning)' }}>
            {vendor?.is_active ? t('vendorWorking') : t('shopClosed')}
          </strong>
        </section>
      </div>

      {vendorError ? (
        <section className="courier-section">
          <EmptyState
            tone="error"
            title={t('shopLoadError')}
            text={vendorError}
            actionLabel={t('retry')}
            onAction={onRetryVendor}
          />
        </section>
      ) : vendor ? (
        <section className="courier-section">
          <div className="courier-section-head">
            <h2>{t('readiness')}</h2>
            <span className="courier-pill">
              {readyDone} {t('readyOf')} 3
            </span>
          </div>
          <ul className="courier-checklist">
            <li className="card courier-check">
              <span className={`courier-check-mark${vendor.products_count > 0 ? '' : ' warning'}`}>
                {vendor.products_count > 0 ? '✓' : '!'}
              </span>
              <span>{t('shopNeedProducts')}</span>
              <span className="muted">{vendor.products_count}</span>
            </li>
            <li className="card courier-check">
              <span className={`courier-check-mark${vendor.zones_count > 0 ? '' : ' warning'}`}>
                {vendor.zones_count > 0 ? '✓' : '!'}
              </span>
              <span>{t('shopNeedZones')}</span>
              <span className="muted">{vendor.zones_count}</span>
            </li>
            <li className="card courier-check">
              <span className={`courier-check-mark${vendor.open_days_count > 0 ? '' : ' warning'}`}>
                {vendor.open_days_count > 0 ? '✓' : '!'}
              </span>
              <span>{t('shopNeedSchedule')}</span>
              <span className="muted">{vendor.open_days_count}/7</span>
            </li>
          </ul>
        </section>
      ) : (
        <section className="courier-section">
          <EmptyState title={t('shopNone')} text={t('shopNoneHint')} />
        </section>
      )}

      <section className="courier-section">
        <div className="courier-section-head">
          <h2>{t('quickActions')}</h2>
        </div>
        <div className="courier-quick-grid">
          <QuickAction icon="orders" title={t('currentOrders')} subtitle={t('currentOrdersHint')} onClick={onOrders} />
          <QuickAction icon="order" title={t('shopSectionProducts')} subtitle={t('productPriceAvailability')} onClick={onProducts} />
          <QuickAction icon="location" title={t('shopSectionZones')} subtitle={t('zoneMapDelivery')} onClick={onZones} />
          <QuickAction icon="profile" title={t('profile')} subtitle={t('profileContactPayment')} onClick={onProfile} />
        </div>
      </section>
    </main>
  );
}
